import { useMemo } from "react";
import { useMutation, useQuery, UseQueryResult } from "@tanstack/react-query";
import { ServerInferRequest } from "@ts-rest/core";
import { accountContract } from "@common/contracts/account.contract";
import { flatReportAccountSortComparator } from "@common/helpers/accounting";
import { useShowAccountNumbers } from "@hooks/useShowAccountNumbers";
import { MergeAccountsPreviewResponse } from "@common/schemas/domains/accounting/account.schema";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useScope } from "@state/scopeContext.lib";
import {
    Account,
    AccountGroup,
    GetAccountsResponse,
} from "@common/types/domains/accounting/account";
import { EntityAccountGroupSchema } from "@common/schemas/domains/accounting/account-group.schema";
import { ExtendedAccount } from "@common/types/reports/types";
import { getAccountsQueryKey } from "@hooks/useAccounts";
import { useIsAccrualLedgerEnabled } from "@hooks/useIsAccrualLedgerEnabled";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { queryClient } from "../queryClient";
import { form1099NecReportQueryKey } from "./form1099NecReport.api";
import {
    accountingReportsQueryKeys,
    ChartOfAccountsQueryData,
} from "./accountingReports.api";

const accountContractClient = getBackendAPIClient(accountContract);

export const getWorkspaceAccountsKey = (workspaceId: string) => [
    "workspace-accounts",
    workspaceId,
];

const getWorkspaceAccrualAccountsKey = (workspaceId: string) => [
    "workspace-accrual-accounts",
    workspaceId,
];

type WorkspaceAccountsByEntity = Array<{
    entityId: number;
    accounts: Account[];
    groups: EntityAccountGroupSchema[];
}>;

async function fetchWorkspaceAccounts({
    workspaceId,
    ledgerBasis,
}: {
    workspaceId: string | null | undefined;
    ledgerBasis: AccountingLedgerBasis;
}): Promise<WorkspaceAccountsByEntity | undefined> {
    if (!workspaceId) {
        return;
    }
    const result = await accountContractClient.getAccountsForWorkspace({
        params: {
            workspaceId,
        },
        query: {
            ledgerBasis,
        },
    });
    return result.body.accounts;
}

function flattenWorkspaceAccounts(
    entityAccounts: WorkspaceAccountsByEntity | undefined,
) {
    return entityAccounts?.flatMap((a) =>
        a.accounts.map((account) => ({
            ...account,
            entityId: a.entityId,
        })),
    );
}

function toGroupsByEntityId(
    entityAccounts: WorkspaceAccountsByEntity | undefined,
): Map<number, AccountGroup[]> {
    return new Map(entityAccounts?.map((a) => [a.entityId, a.groups]) ?? []);
}

export function useWorkspaceAccounts(options?: { enabled?: boolean }) {
    const { workspaceId } = useScope();
    return useQuery({
        queryKey: getWorkspaceAccountsKey(workspaceId ?? ""),
        queryFn: async () =>
            await fetchWorkspaceAccounts({
                workspaceId,
                ledgerBasis: AccountingLedgerBasis.cash,
            }),
        select: flattenWorkspaceAccounts,
        enabled: options?.enabled ?? true,
        refetchOnMount: false,
        staleTime: 1000 * 60 * 30, // 30 minutes
    });
}

/**
 * Second projection of the cash workspace-accounts request: the account group
 * rows per business, keyed by entity. Groups carry no ledger basis, so the
 * accrual picker reads this hook too.
 */
export function useWorkspaceAccountGroups(): UseQueryResult<
    Map<number, AccountGroup[]>
> {
    const { workspaceId } = useScope();
    return useQuery({
        queryKey: getWorkspaceAccountsKey(workspaceId ?? ""),
        queryFn: async () =>
            await fetchWorkspaceAccounts({
                workspaceId,
                ledgerBasis: AccountingLedgerBasis.cash,
            }),
        select: toGroupsByEntityId,
        refetchOnMount: false,
        staleTime: 1000 * 60 * 30, // 30 minutes
    });
}

export function useWorkspaceAccrualAccounts(options?: { enabled?: boolean }) {
    const { workspaceId } = useScope();
    const isAccrualLedgerEnabled = useIsAccrualLedgerEnabled();

    return useQuery({
        queryKey: getWorkspaceAccrualAccountsKey(workspaceId ?? ""),
        queryFn: async () =>
            await fetchWorkspaceAccounts({
                workspaceId,
                ledgerBasis: AccountingLedgerBasis.accruals,
            }),
        select: flattenWorkspaceAccounts,
        enabled:
            (options?.enabled ?? true) &&
            !!workspaceId &&
            isAccrualLedgerEnabled,
        refetchOnMount: false,
        staleTime: 1000 * 60 * 30, // 30 minutes
    });
}

const getCoaLastUpdatedByEntityIdsQueryKey = (entityIds: number[]) => [
    "coa-last-updated",
    "byEntityIds",
    entityIds,
];

/**
 * Resolves the last CoA change (MAX account.updatedAt) per entity. Mirrors
 * `useLedgersByEntityIds`: dedupes ids, skips the request when there are none,
 * and returns a `Record<entityId, ISO string | null>` (null when the entity has
 * no accounts / no recorded change).
 */
export function useCoaLastUpdatedByEntityIds(entityIds: number[]) {
    const uniqueEntityIds = useMemo(
        () => [...new Set(entityIds)].filter((id) => !!id),
        [entityIds],
    );

    return useQuery({
        queryKey: getCoaLastUpdatedByEntityIdsQueryKey(uniqueEntityIds),
        queryFn: async (): Promise<Record<number, string | null>> => {
            if (uniqueEntityIds.length === 0) {
                return {};
            }
            const result = await accountContractClient.getCoaLastUpdated({
                query: { entityIds: uniqueEntityIds },
            });
            return result.body.coaLastUpdatedByEntityId;
        },
        refetchOnMount: false,
        staleTime: 1000 * 60 * 5, // 5 minutes
    });
}

const getMergeAccountsPreviewQueryKey = ({
    entityId,
    sourceAccountId,
    targetAccountId,
}: {
    entityId: number;
    sourceAccountId: string;
    targetAccountId: string | undefined;
}) => [
    "merge-accounts-preview",
    "byAccountPair",
    entityId,
    sourceAccountId,
    targetAccountId,
];

export function useMergeAccountsPreview({
    entityId,
    sourceAccountId,
    targetAccountId,
}: {
    entityId: number;
    sourceAccountId: string;
    targetAccountId: string | undefined;
}) {
    return useQuery({
        queryKey: getMergeAccountsPreviewQueryKey({
            entityId,
            sourceAccountId,
            targetAccountId,
        }),
        queryFn: async (): Promise<MergeAccountsPreviewResponse> => {
            if (!targetAccountId) {
                throw new Error("Target account is required");
            }
            const result = await accountContractClient.mergeAccountsPreview({
                params: { entityId },
                query: { sourceAccountId, targetAccountId },
            });
            return result.body;
        },
        enabled: !!sourceAccountId && !!targetAccountId,
    });
}

interface MergeAccountsMutationParams {
    entityId: number;
    body: ServerInferRequest<typeof accountContract.mergeAccounts>["body"];
}

export function useMergeAccountsMutation() {
    const { workspaceId } = useScope();
    const { toast } = useToaster();

    return useMutation({
        mutationFn: async ({ entityId, body }: MergeAccountsMutationParams) => {
            const response = await accountContractClient.mergeAccounts({
                params: { entityId },
                body,
            });
            return response.body;
        },
        onSuccess: (data, { entityId }) => {
            invalidateAccountRelatedQueries({
                entityIds: [entityId],
                workspaceId,
            });
            invalidateLedgerDataQueries();
            toast(data.message);
        },
    });
}

interface ReassignAccountRoleMutationParams {
    entityId: number;
    body: ServerInferRequest<
        typeof accountContract.reassignAccountRole
    >["body"];
}

export function useReassignAccountRoleMutation() {
    const { workspaceId } = useScope();

    return useMutation({
        mutationFn: async ({
            entityId,
            body,
        }: ReassignAccountRoleMutationParams) => {
            const response = await accountContractClient.reassignAccountRole({
                params: { entityId },
                body,
            });
            return response.body;
        },
        onSuccess: (_data, { entityId }) => {
            invalidateAccountRelatedQueries({
                entityIds: [entityId],
                workspaceId,
            });
            invalidateLedgerDataQueries();
        },
    });
}

export function useCreateAccountMutation() {
    const { workspaceId } = useScope();
    const showAccountNumbers = useShowAccountNumbers();

    return useMutation({
        mutationFn: async (
            body: ServerInferRequest<
                typeof accountContract.createAccount
            >["body"],
        ) => {
            if (!workspaceId) {
                throw new Error("Workspace ID is required");
            }

            const response = await accountContractClient.createAccount({
                params: { workspaceId },
                body,
            });
            return response.body;
        },
        onSuccess: async (data) => {
            optimisticallyAddAccountInRelatedQueries(
                data.accounts,
                showAccountNumbers,
            );
            invalidateAccountRelatedQueries({
                entityIds: data.accounts.map((a) => a.entityId),
                workspaceId,
            });
        },
    });
}

export function useBulkUpdateAccountsMutation() {
    const { workspaceId } = useScope();

    return useMutation({
        mutationFn: async (
            body: ServerInferRequest<
                typeof accountContract.bulkUpdateAccounts
            >["body"],
        ) => {
            const response = await accountContractClient.bulkUpdateAccounts({
                body,
            });
            return response.body.accounts;
        },
        onSuccess: async (accounts, body) => {
            const entityIds = Array.from(
                new Set(body.accounts.map((item) => item.entityId)),
            );

            for (const account of accounts) {
                const bodyItem = body.accounts.find(
                    (item) => item.accountId === account.id,
                );
                if (bodyItem) {
                    optimisticallyUpdateAccountInRelatedQueries(
                        account.id,
                        bodyItem.entityId,
                        {
                            name: account.name,
                            type: account.type,
                            subtype: account.subtype,
                            code: account.code,
                            codeText: account.codeText,
                            form1099Box: account.form1099Box,
                        },
                    );
                }
            }

            invalidateAccountRelatedQueries({ entityIds, workspaceId });
        },
    });
}

export function useDisableAccountsBulkMutation() {
    const { workspaceId } = useScope();

    return useMutation({
        mutationFn: async (
            body: ServerInferRequest<
                typeof accountContract.disableAccountsBulk
            >["body"],
        ) => {
            const response = await accountContractClient.disableAccountsBulk({
                body,
            });
            return response.body;
        },
        onSuccess: async (results, body) => {
            const entityIds = Array.from(
                new Set(body.map((item) => item.entityId)),
            );

            // Optimistically update accounts that were successfully disabled
            for (const result of results) {
                if (result.success) {
                    const bodyItem = body.find(
                        (item) => item.accountId === result.id,
                    );
                    if (bodyItem) {
                        optimisticallyUpdateAccountInRelatedQueries(
                            result.id,
                            bodyItem.entityId,
                            {
                                isDisabled: true,
                            },
                        );
                    }
                }
            }

            invalidateAccountRelatedQueries({ entityIds, workspaceId });
        },
    });
}

export function useEnableAccountsBulkMutation() {
    const { workspaceId } = useScope();

    return useMutation({
        mutationFn: async (
            body: ServerInferRequest<
                typeof accountContract.enableAccountsBulk
            >["body"],
        ) => {
            const response = await accountContractClient.enableAccountsBulk({
                body,
            });
            return response.body;
        },
        onSuccess: async (results, body) => {
            const entityIds = Array.from(
                new Set(body.map((item) => item.entityId)),
            );

            // Optimistically update accounts that were successfully enabled
            for (const result of results) {
                if (result.success) {
                    const bodyItem = body.find(
                        (item) => item.accountId === result.id,
                    );
                    if (bodyItem) {
                        optimisticallyUpdateAccountInRelatedQueries(
                            result.id,
                            bodyItem.entityId,
                            {
                                isDisabled: false,
                            },
                        );
                    }
                }
            }

            invalidateAccountRelatedQueries({ entityIds, workspaceId });
        },
    });
}

export function useDeleteAccountsBulkMutation() {
    const { workspaceId } = useScope();

    return useMutation({
        mutationFn: async (
            body: ServerInferRequest<
                typeof accountContract.deleteAccountsBulk
            >["body"],
        ) => {
            const response = await accountContractClient.deleteAccountsBulk({
                body,
            });
            return response.body;
        },
        onSuccess: async (results, body) => {
            const entityIds = Array.from(
                new Set(body.map((item) => item.entityId)),
            );

            // Optimistically delete accounts that were successfully deleted
            for (const result of results) {
                if (result.success) {
                    const bodyItem = body.find(
                        (item) => item.accountId === result.id,
                    );
                    if (bodyItem) {
                        optimisticallyDeleteAccountInRelatedQueries(
                            result.id,
                            bodyItem.entityId,
                        );
                    }
                }
            }

            invalidateAccountRelatedQueries({ entityIds, workspaceId });
        },
    });
}

/**
 * A merge repoints journal entry lines onto the target and deletes the source,
 * so every cache derived from the ledger is stale, not just the account lists.
 */
function invalidateLedgerDataQueries() {
    for (const queryKey of Object.values(accountingReportsQueryKeys)) {
        queryClient.invalidateQueries({ queryKey: [queryKey] });
    }
    queryClient.invalidateQueries({ queryKey: ["transactions"] });
}

interface InvalidateAccountRelatedQueriesParams {
    entityIds?: number[];
    workspaceId?: string | null;
}

export function invalidateAccountRelatedQueries({
    entityIds,
    workspaceId,
}: InvalidateAccountRelatedQueriesParams) {
    queryClient.invalidateQueries({
        queryKey: [accountingReportsQueryKeys.chartOfAccounts],
    });

    if (entityIds?.length) {
        for (const entityId of entityIds) {
            queryClient.invalidateQueries({
                queryKey: getAccountsQueryKey(entityId),
            });
        }
    } else {
        queryClient.invalidateQueries({
            queryKey: getAccountsQueryKey(),
        });
    }
    if (workspaceId) {
        queryClient.invalidateQueries({
            queryKey: getWorkspaceAccountsKey(workspaceId),
        });
        queryClient.invalidateQueries({
            queryKey: [form1099NecReportQueryKey, workspaceId],
        });
        queryClient.invalidateQueries({
            queryKey: getWorkspaceAccrualAccountsKey(workspaceId),
        });
    }
}

function optimisticallyDeleteAccountInRelatedQueries(
    accountId: string,
    entityId: number,
) {
    const allCoaQueries = queryClient.getQueriesData<ChartOfAccountsQueryData>({
        queryKey: [accountingReportsQueryKeys.chartOfAccounts],
    });

    for (const [queryKey, data] of allCoaQueries) {
        if (data) {
            queryClient.setQueryData<ChartOfAccountsQueryData>(queryKey, {
                ...data,
                accounts: data.accounts.filter(
                    (account) => account.id !== accountId,
                ),
            });
        }
    }

    const accountsData = queryClient.getQueryData<GetAccountsResponse>(
        getAccountsQueryKey(entityId),
    );
    if (accountsData) {
        const { accounts } = accountsData;
        const filteredAccounts = accounts?.filter((a) => a.id !== accountId);
        queryClient.setQueryData(getAccountsQueryKey(entityId), {
            accounts: filteredAccounts,
        });
    }
}

function optimisticallyUpdateAccountInRelatedQueries(
    accountId: string,
    entityId: number,
    update: Partial<Account>,
) {
    const allCoaQueries = queryClient.getQueriesData<ChartOfAccountsQueryData>({
        queryKey: [accountingReportsQueryKeys.chartOfAccounts],
    });

    for (const [queryKey, data] of allCoaQueries) {
        if (data) {
            queryClient.setQueryData<ChartOfAccountsQueryData>(queryKey, {
                ...data,
                accounts: data.accounts.map((account) =>
                    account.id === accountId
                        ? { ...account, ...update }
                        : account,
                ),
            });
        }
    }

    const accountsData = queryClient.getQueryData<GetAccountsResponse>(
        getAccountsQueryKey(entityId),
    );
    if (accountsData) {
        const { accounts } = accountsData;
        const newAccounts = accounts?.map((a) =>
            a.id === accountId ? { ...a, ...update } : a,
        );
        queryClient.setQueryData(getAccountsQueryKey(entityId), {
            accounts: newAccounts,
        });
    }
}

function optimisticallyAddAccountInRelatedQueries(
    addedAccounts: ExtendedAccount[],
    showAccountNumbers: boolean,
) {
    const allCoaQueries = queryClient.getQueriesData<ChartOfAccountsQueryData>({
        queryKey: [accountingReportsQueryKeys.chartOfAccounts],
    });

    for (const [queryKey, data] of allCoaQueries) {
        if (data) {
            queryClient.setQueryData<ChartOfAccountsQueryData>(queryKey, {
                ...data,
                accounts: [...data.accounts, ...addedAccounts],
            });
        }
    }

    for (const account of addedAccounts) {
        const accountsData = queryClient.getQueryData<GetAccountsResponse>(
            getAccountsQueryKey(account.entityId),
        );
        if (accountsData) {
            const { accounts } = accountsData;
            const newAccounts = [...accounts, account].sort((a, b) =>
                flatReportAccountSortComparator(a, b, showAccountNumbers),
            );
            queryClient.setQueryData(getAccountsQueryKey(account.entityId), {
                accounts: newAccounts,
            });
        }
    }
}
